/**
 * Batch 18-K108 — useCompanionReadiness
 *
 * Maintains a cached Companion-installed state via the tokenless PING/PONG.
 * A background ping runs on mount (when enabled) so that, by the time a user
 * clicks "Open", the last known readiness is available synchronously — which
 * keeps the actual monitored launch `window.open` inside the click gesture
 * (popup-safe). `refresh()` re-pings on demand (e.g. the modal's "check again").
 *
 * No token is involved. Logs only "installed / not installed", never a token.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  pingCompanion,
  type CompanionReadinessState,
} from '@/lib/trainingCompanion/companionAvailability';

export interface UseCompanionReadinessResult {
  state: CompanionReadinessState;
  /** Re-ping now; resolves true when the Companion answered. */
  refresh: () => Promise<boolean>;
}

export function useCompanionReadiness(
  { enabled = true }: { enabled?: boolean } = {},
): UseCompanionReadinessResult {
  const [state, setState] = useState<CompanionReadinessState>('unknown');
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async (): Promise<boolean> => {
    const { installed } = await pingCompanion();
    if (mounted.current) {
      setState(installed ? 'installed' : 'missing');
    }
     
    console.log(`[Companion] readiness: ${installed ? 'installed' : 'not installed'}`);
    return installed;
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
  }, [enabled, refresh]);

  return { state, refresh };
}
